import React, { useMemo, useState } from "react";
import { t } from "ttag";
import { BaseDialog, Button, Spinner, Table, TextField } from "@coolremote/shared";
import { useGetSiteModbusUiTemplates } from "../../../../hooks/useModbusUiTemplatesApi";
import { IModbusFunctionalRow, IModbusUiTemplate } from "../../types";
import type { IUIControlPoint, IUITemplate } from "../../UITemplateMapper";
import styles from "./ControlUiLibraryDialog.module.css";

export interface IControlUiSelection {
  rows: IModbusFunctionalRow[];
  defaultUiTemplate: IUITemplate;
}

interface IProps {
  open: boolean;
  siteId?: string;
  onClose: () => void;
  /** When provided, each row gets a "Select" action that hands back the UI template plus the rows its points were mapped against. */
  onSelect?: (selection: IControlUiSelection) => void;
}

interface IControlUiRow {
  id: string;
  uiName: string;
  unitName: string;
  mappedParams: string[];
  selection: IControlUiSelection;
}

/** Library of Control UIs — the dedicated modbus-ui-templates collection for this site. */
const ControlUiLibraryDialog: React.FC<IProps> = ({ open, siteId, onClose, onSelect }) => {
  const [search, setSearch] = useState("");

  const { data, isLoading } = useGetSiteModbusUiTemplates(siteId, open) as unknown as {
    data: IModbusUiTemplate[] | null;
    isLoading: boolean;
  };

  const rows = useMemo<IControlUiRow[]>(() => {
    return (data ?? [])
      .map((tpl) => {
        const templateRows = (tpl.data?.rows ?? []) as IModbusFunctionalRow[];
        const points = (tpl.data?.points ?? []) as IUIControlPoint[];
        // A point's display label wins; fall back to the mapped register row's
        // parameter name via rowIndex.
        const mappedParams = points.map((p) => p.labelName?.trim() || templateRows[p.rowIndex]?.name || t`Unnamed`);
        return {
          id: tpl.id as string,
          uiName: tpl.name || t`Unnamed`,
          unitName: tpl.data?.functionalUnitName?.trim() || "–",
          mappedParams,
          selection: {
            rows: templateRows,
            defaultUiTemplate: {
              name: tpl.name,
              backgroundImage: tpl.data?.backgroundImage,
              points,
            },
          },
        };
      });
  }, [data]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter(
      (r) =>
        r.uiName.toLowerCase().includes(term) ||
        r.unitName.toLowerCase().includes(term) ||
        r.mappedParams.some((p) => p.toLowerCase().includes(term)),
    );
  }, [rows, search]);

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title={t`Control UI Library`}
      hideConfirm
      cancelLabel={t`Close`}
      cancelSize="medium"
      size="large"
    >
      <div className={styles.searchWrap}>
        <TextField
          fullWidth
          placeholder={t`Search by name`}
          value={search}
          onChange={(v: string) => setSearch(v)}
        />
      </div>

      {isLoading ? (
        <div className={styles.loaderWrap}>
          <Spinner size={28} />
        </div>
      ) : (
        <Table
          data={filtered}
          getRowKey={(row: IControlUiRow, index: number) => row.id ?? index}
          emptyMessage={
            search
              ? t`No control UIs match your search.`
              : t`No control UIs found for this site. Create one from a functional unit's Control UI field.`
          }
          columns={[
            {
              id: "uiName",
              title: t`CONTROL UI NAME`,
              width: "30%",
              strong: true,
              render: (row: IControlUiRow) => row.uiName,
            },
            {
              id: "unitName",
              title: t`FUNCTIONAL UNIT NAME`,
              width: "30%",
              render: (row: IControlUiRow) => row.unitName,
            },
            {
              id: "mappedParams",
              title: t`MAPPED PARAMETERS`,
              width: onSelect ? "30%" : "40%",
              render: (row: IControlUiRow) => (
                <div className={styles.paramList} title={row.mappedParams.join(", ")}>
                  {row.mappedParams.length > 0 ? row.mappedParams.join(", ") : "–"}
                </div>
              ),
            },
            ...(onSelect ? [{
              id: "action",
              width: "10%",
              align: "right" as const,
              render: (row: IControlUiRow) => (
                <Button white size="small" onClick={() => onSelect(row.selection)}>
                  {t`Select`}
                </Button>
              ),
            }] : []),
          ]}
        />
      )}
    </BaseDialog>
  );
};

export default ControlUiLibraryDialog;
